import React from 'react';

import { useWindowSize } from '../../hooks/use-window-size';

/**
 * メニューポップオーバーのプロパティ
 */
export interface MenuPopoverProps {
  /** ポップオーバーの表示状態 */
  visible: boolean;
  /** 権利表記メニューがクリックされたときのコールバック */
  onLicenseClick: () => void;
  /** 操作説明メニューがクリックされたときのコールバック */
  onOperationGuideClick?: () => void;
  /** クオリティ調整メニューがクリックされたときのコールバック */
  onQualityClick?: () => void;
  /** ポップオーバーを閉じるときのコールバック */
  onClose: () => void;
}

/**
 * メニューポップオーバーコンポーネント
 *
 * ヘッダーのメニューボタンをクリックしたときに表示されるポップオーバーメニュー
 */
export function MenuPopover({
  visible,
  onLicenseClick,
  onOperationGuideClick,
  onQualityClick,
  onClose
}: MenuPopoverProps) {
  // ウィンドウサイズを取得
  const { width } = useWindowSize();

  // GoogleフォントのMaterial Symbols（Rounded版）を読み込む
  React.useEffect(() => {
    const link = document.createElement('link');
    link.href =
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // ウィンドウサイズに応じてスタイルを調整
  const isSmallScreen = width < 768;
  const isMediumScreen = width >= 768 && width < 1024;

  // 動的なスタイル計算
  const menuWidth = isSmallScreen ? '180px' : '250px';
  const menuRight = isSmallScreen ? '10px' : isMediumScreen ? '20px' : '88px';
  const fontSize = isSmallScreen ? '14px' : '16px';

  // ビルド日時を取得
  const buildTime = process.env.NEXT_PUBLIC_BUILD_TIME || '';

  // モーダルが非表示の場合は何も表示しない
  if (!visible) {
    return null;
  }

  return (
    <>
      {/* 背景オーバーレイ（クリックでメニューを閉じる） */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1999,
          backgroundColor: 'transparent'
        }}
        onClick={onClose}
      />

      {/* メニューポップオーバー本体 */}
      <div
        style={{
          position: 'absolute',
          top: '60px',
          right: menuRight,
          width: menuWidth,
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '8px 0',
          zIndex: 2000,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
        }}
      >
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0
          }}
        >
          <li>
            <button
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                color: '#4C4948',
                padding: '10px 16px',
                textAlign: 'left',
                fontSize: fontSize,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={onOperationGuideClick}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
              }}
            >
              <span>操作説明</span>
              <span
                className='material-symbols-rounded'
                style={{
                  color: '#4C4948',
                  fontSize: 24,
                  fontFamily: 'Material Symbols Rounded',
                  fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"
                }}
              >
                help
              </span>
            </button>
          </li>
          <li>
            <button
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                color: '#4C4948',
                padding: '10px 16px',
                textAlign: 'left',
                fontSize: fontSize,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={onQualityClick}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
              }}
            >
              <span>設定</span>
              <span
                className='material-symbols-rounded'
                style={{
                  color: '#4C4948',
                  fontSize: 24,
                  fontFamily: 'Material Symbols Rounded',
                  fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"
                }}
              >
                tune
              </span>
            </button>
          </li>
          <li>
            <button
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                color: '#4C4948',
                padding: '10px 16px',
                textAlign: 'left',
                fontSize: fontSize,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={onLicenseClick}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
              }}
            >
              <span>権利表記</span>
              <span
                className='material-symbols-rounded'
                style={{
                  color: '#4C4948',
                  fontSize: 24,
                  fontFamily: 'Material Symbols Rounded',
                  fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"
                }}
              >
                description
              </span>
            </button>
          </li>
        </ul>

        {/* ビルド日時表示 */}
        {buildTime && (
          <div
            style={{
              padding: '4px 16px 0px 16px',
              fontSize: '11px',
              color: 'rgba(76, 73, 72, 0.5)',
              textAlign: 'center',
              borderTop: '1px solid rgba(0, 0, 0, 0.1)',
              marginTop: '0px'
            }}
          >
            {buildTime}
          </div>
        )}
      </div>
    </>
  );
}
